// ─── Template type enum matching backend ─────────────────────────────────────

export const AiPromptTemplateType = {
  Reply: 0,
  Draft: 1,
  Summary: 2,
} as const;

export type AiPromptTemplateTypeValue = (typeof AiPromptTemplateType)[keyof typeof AiPromptTemplateType];

export const AiPromptUseCase = {
  ReplyDefault: 'conversation.reply',
  ReplyMention: 'conversation.reply.mention',
  ReplyExternalUnmuted: 'conversation.reply.external.unmuted',
  ReplyInstagramComment: 'conversation.reply.instagram.comment',
  ReplyMessengerComment: 'conversation.reply.messenger.comment',
  Draft: 'conversation.draft',
  Summary: 'conversation.summary',
} as const;

export type AiExternalChannelKey = 'whatsapp' | 'telegram' | 'instagram' | 'messenger' | 'webchat' | 'tiktok';

export type AiPromptUseCaseId = (typeof AiPromptUseCase)[keyof typeof AiPromptUseCase] | `${typeof AiPromptUseCase.ReplyExternalUnmuted}.${AiExternalChannelKey}`;

export const AI_EXTERNAL_CHANNEL_KEYS: AiExternalChannelKey[] = [
  'whatsapp',
  'telegram',
  'instagram',
  'messenger',
  'webchat',
  'tiktok',
];

export const buildExternalUnmutedUseCaseId = (channelKey?: AiExternalChannelKey | 'all' | null): AiPromptUseCaseId => {
  if (!channelKey || channelKey === 'all') {
    return AiPromptUseCase.ReplyExternalUnmuted;
  }

  return `${AiPromptUseCase.ReplyExternalUnmuted}.${channelKey}` as AiPromptUseCaseId;
};

export const isExternalUnmutedUseCase = (useCaseId: string): boolean =>
  useCaseId === AiPromptUseCase.ReplyExternalUnmuted
  || useCaseId.startsWith(`${AiPromptUseCase.ReplyExternalUnmuted}.`);

export const isChannelSpecificExternalUnmutedUseCase = (useCaseId: string): boolean =>
  useCaseId.startsWith(`${AiPromptUseCase.ReplyExternalUnmuted}.`);

export const isCommentSurfaceUseCase = (useCaseId: string): boolean =>
  useCaseId === AiPromptUseCase.ReplyInstagramComment
  || useCaseId === AiPromptUseCase.ReplyMessengerComment;

export const usesDedicatedPromptOverride = (useCaseId: string): boolean =>
  isChannelSpecificExternalUnmutedUseCase(useCaseId) || isCommentSurfaceUseCase(useCaseId);

export interface AiExternalChannelOverrideStatus {
  channelKey: string;
  useCaseId: string;
  isCustomized: boolean;
}

export type AiSettingsTabId = 'general' | AiPromptUseCaseId;

export const AI_SETTINGS_GENERAL_TAB = {
  id: 'general' as const,
  tabLabel: 'General',
  title: 'General Settings',
  description: 'Configure AI providers and workspace-wide assistant defaults.',
};

export type AiPromptToolPolicy = 'tenantAllowed' | 'disabled';

export interface AiPromptUseCaseTabConfig {
  id: AiPromptUseCaseId;
  tabLabel: string;
  title: string;
  description: string;
  templateType: AiPromptTemplateTypeValue;
  toolPolicy: AiPromptToolPolicy;
  templateScopeNote?: string;
}

export const AI_PROMPT_USE_CASE_TABS: AiPromptUseCaseTabConfig[] = [
  {
    id: AiPromptUseCase.ReplyDefault,
    tabLabel: 'Reply',
    title: 'Conversation Reply',
    description: 'Default reply behavior for normal conversation responses.',
    templateType: AiPromptTemplateType.Reply,
    toolPolicy: 'tenantAllowed',
  },
  {
    id: AiPromptUseCase.ReplyMention,
    tabLabel: 'Reply Mention',
    title: 'Mention-triggered Reply',
    description: 'Reply behavior when the latest message explicitly mentions the AI assistant.',
    templateType: AiPromptTemplateType.Reply,
    toolPolicy: 'disabled',
    templateScopeNote: 'Shares the same prompt template as Conversation Reply.',
  },
  {
    id: AiPromptUseCase.ReplyExternalUnmuted,
    tabLabel: 'External Unmuted',
    title: 'External Unmuted Auto-Reply',
    description: 'Reply behavior for external conversations where AI mute is turned off.',
    templateType: AiPromptTemplateType.Reply,
    toolPolicy: 'disabled',
    templateScopeNote: 'The All channels prompt shares the Conversation Reply template until a channel-specific override is saved.',
  },
  {
    id: AiPromptUseCase.Draft,
    tabLabel: 'Draft',
    title: 'Employee Draft',
    description: 'Generates suggested replies for employees to review and edit before sending.',
    templateType: AiPromptTemplateType.Draft,
    toolPolicy: 'tenantAllowed',
  },
  {
    id: AiPromptUseCase.Summary,
    tabLabel: 'Summary',
    title: 'Conversation Summary',
    description: 'Creates a concise summary for employees to quickly understand the conversation.',
    templateType: AiPromptTemplateType.Summary,
    toolPolicy: 'tenantAllowed',
  },
];

export const AI_INSTAGRAM_COMMENT_USE_CASE: AiPromptUseCaseTabConfig = {
  id: AiPromptUseCase.ReplyInstagramComment,
  tabLabel: 'Instagram comments',
  title: 'Instagram Comment Auto-Reply',
  description: 'Reply behavior for Instagram comment threads (post and live comments). Separate from Instagram DMs.',
  templateType: AiPromptTemplateType.Reply,
  toolPolicy: 'tenantAllowed',
};

export const AI_MESSENGER_COMMENT_USE_CASE: AiPromptUseCaseTabConfig = {
  id: AiPromptUseCase.ReplyMessengerComment,
  tabLabel: 'Facebook comments',
  title: 'Facebook Comment Auto-Reply',
  description: 'Reply behavior for Facebook Page comment threads. Separate from Messenger DMs.',
  templateType: AiPromptTemplateType.Reply,
  toolPolicy: 'tenantAllowed',
};

export type ExternalChannelRailItemId = 'all' | AiExternalChannelKey | 'instagram-comments' | 'messenger-comments';

export type ExternalChannelRailItem = {
  id: ExternalChannelRailItemId;
  useCaseId: AiPromptUseCaseId;
  iconKind: 'whatsApp' | 'telegram' | 'instagram' | 'messenger' | 'webchat' | 'tiktok' | null;
  nested?: boolean;
  inheritsFromAllChannels: boolean;
};

export const AI_EXTERNAL_CHANNEL_RAIL_ITEMS: ExternalChannelRailItem[] = [
  {
    id: 'all',
    useCaseId: AiPromptUseCase.ReplyExternalUnmuted,
    iconKind: null,
    inheritsFromAllChannels: false,
  },
  {
    id: 'whatsapp',
    useCaseId: buildExternalUnmutedUseCaseId('whatsapp'),
    iconKind: 'whatsApp',
    inheritsFromAllChannels: true,
  },
  {
    id: 'telegram',
    useCaseId: buildExternalUnmutedUseCaseId('telegram'),
    iconKind: 'telegram',
    inheritsFromAllChannels: true,
  },
  {
    id: 'instagram',
    useCaseId: buildExternalUnmutedUseCaseId('instagram'),
    iconKind: 'instagram',
    inheritsFromAllChannels: true,
  },
  {
    id: 'instagram-comments',
    useCaseId: AiPromptUseCase.ReplyInstagramComment,
    iconKind: 'instagram',
    nested: true,
    inheritsFromAllChannels: false,
  },
  {
    id: 'messenger',
    useCaseId: buildExternalUnmutedUseCaseId('messenger'),
    iconKind: 'messenger',
    inheritsFromAllChannels: true,
  },
  {
    id: 'messenger-comments',
    useCaseId: AiPromptUseCase.ReplyMessengerComment,
    iconKind: 'messenger',
    nested: true,
    inheritsFromAllChannels: false,
  },
  {
    id: 'webchat',
    useCaseId: buildExternalUnmutedUseCaseId('webchat'),
    iconKind: 'webchat',
    inheritsFromAllChannels: true,
  },
  {
    id: 'tiktok',
    useCaseId: buildExternalUnmutedUseCaseId('tiktok'),
    iconKind: 'tiktok',
    inheritsFromAllChannels: true,
  },
];

export const AI_PROMPT_TEMPLATE_TYPE_LABELS: Record<AiPromptTemplateTypeValue, string> = {
  [AiPromptTemplateType.Reply]: 'Customer Auto-Reply',
  [AiPromptTemplateType.Draft]: 'Employee Draft',
  [AiPromptTemplateType.Summary]: 'Conversation Summary',
};

export const AI_PROMPT_TEMPLATE_DESCRIPTIONS: Record<AiPromptTemplateTypeValue, string> = {
  [AiPromptTemplateType.Reply]: 'Automatically replies to customer messages. This is what customers receive.',
  [AiPromptTemplateType.Draft]: 'Generates suggested replies for employees to review and edit before sending.',
  [AiPromptTemplateType.Summary]: 'Creates a bullet-point summary of the conversation for employees.',
};

// ─── API Response & Request types ────────────────────────────────────────────

export interface AiPromptConfig {
  templateType: AiPromptTemplateTypeValue;
  systemPrompt: string | null;
  toneOfVoice: string | null;
  includeCompanyKnowledge: boolean;
  includeConversationHistory: boolean;
  maxTokensResponse: number | null;
  customFields: string | null;
  version: number;
  isDefault: boolean;
  isInherited?: boolean;
  inheritsFromUseCaseId?: string | null;
  useCaseId?: string | null;
  availableVariables: AiPromptTemplateVariable[];
}

export interface AiPromptTemplateVariable {
  name: string;
  description: string;
  example: string;
}

export interface AiPromptVersion {
  version: number;
  systemPrompt: string;
  toneOfVoice: string | null;
  includeCompanyKnowledge: boolean;
  includeConversationHistory: boolean;
  maxTokensResponse: number | null;
  customFields: string | null;
  changedAt: string;
  changeReason: string | null;
}

export interface UpdateAiPromptConfigRequest {
  systemPrompt: string;
  toneOfVoice: string | null;
  includeCompanyKnowledge: boolean;
  includeConversationHistory: boolean;
  maxTokensResponse: number | null;
  customFields: string | null;
  changeReason: string | null;
}

export interface AiKernelFunctionToolPlugin {
  pluginId: string;
  displayName: string;
  kernelPluginName: string;
  isPublished: boolean;
}

export interface AiKernelFunctionSetting {
  functionId: string;
  functionDisplayName: string;
  pluginName: string;
  accessMode: 'read' | 'write';
  enabled: boolean;
  instruction: string | null;
  supportsInstruction: boolean;
  includeResultsInHistory: boolean;
  supportsHistoryInclusion: boolean;
  toolPlugins: AiKernelFunctionToolPlugin[];
}

export const AI_KERNEL_PLUGIN_INSTRUCTION_MAX_LENGTH = 4000;

export interface AiKernelPluginInstruction {
  functionId: string;
  instruction: string | null;
}

export interface AiToolHistoryInclusion {
  functionId: string;
  includeResultsInHistory: boolean;
}

export interface AiKernelFunctionPolicy {
  useCaseId: string;
  isConfigured: boolean;
  functions: AiKernelFunctionSetting[];
  toolPlugins: AiKernelFunctionToolPlugin[];
}

export interface AiAutoFunctionInvocationPolicy {
  useCaseId: string;
  isConfigured: boolean;
  maxToolCalls: number;
}

export interface AiResourceListPrefetchPreference {
  useCaseId: string;
  isConfigured: boolean;
  enabled: boolean;
}

export type AiChatProviderOption = 'Auto' | 'AzureOpenAI' | 'OpenAI' | 'DeepSeek';

export interface AiProviderSetting {
  id: AiChatProviderOption | 'AzureOpenAI' | 'OpenAI' | 'DeepSeek';
  displayName: string;
  isConfiguredAtDeployment: boolean;
  isTenantConfigured: boolean;
  isConfigured: boolean;
  enabled: boolean;
  effectiveEnabled: boolean;
  model: string | null;
  endpoint: string | null;
}

export interface AiProviderSettings {
  isConfigured: boolean;
  chatProvider: AiChatProviderOption;
  activeChatProvider: string;
  activeChatModelId: string;
  providers: AiProviderSetting[];
}

export interface UpdateAiProviderSettingsRequest {
  chatProvider: AiChatProviderOption;
  azureOpenAiEnabled: boolean;
  openAiEnabled: boolean;
  deepSeekEnabled: boolean;
}

export type AiProviderId = 'AzureOpenAI' | 'OpenAI' | 'DeepSeek';

export interface AiAzureOpenAiConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  endpoint: string;
  chatDeploymentName: string;
  embeddingDeploymentName: string;
  audioTranscriptionDeploymentName: string;
  apiVersion: string;
}

export interface UpdateAiAzureOpenAiConfigurationRequest {
  endpoint?: string;
  apiKey?: string;
  chatDeploymentName?: string;
  embeddingDeploymentName?: string;
  audioTranscriptionDeploymentName?: string;
  apiVersion?: string;
}

export interface AiOpenAiConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  model: string;
  embeddingModel: string;
  temperature: number;
  maxOutputTokens: number;
}

export interface UpdateAiOpenAiConfigurationRequest {
  apiKey?: string;
  model?: string;
  embeddingModel?: string;
  temperature?: number;
  maxOutputTokens?: number;
}

export interface AiDeepSeekConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  endpoint: string;
  model: string;
  thinkingEnabled: boolean;
}

export interface UpdateAiDeepSeekConfigurationRequest {
  apiKey?: string;
  endpoint?: string;
  model?: string;
  thinkingEnabled?: boolean;
}

export type AiProviderConfiguration =
  | AiAzureOpenAiConfiguration
  | AiOpenAiConfiguration
  | AiDeepSeekConfiguration;

export type UpdateAiProviderConfigurationRequest =
  | UpdateAiAzureOpenAiConfigurationRequest
  | UpdateAiOpenAiConfigurationRequest
  | UpdateAiDeepSeekConfigurationRequest;

export const AI_CHAT_PROVIDER_OPTIONS: AiChatProviderOption[] = [
  'Auto',
  'AzureOpenAI',
  'OpenAI',
  'DeepSeek',
];

// ─── Tone of voice options ────────────────────────────────────────────────────

export interface ToneOption {
  value: string;
  label: string;
}

export const TONE_OPTIONS: ToneOption[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly & Approachable' },
  { value: 'empathetic', label: 'Empathetic & Caring' },
  { value: 'technical', label: 'Technical & Precise' },
  { value: 'concise', label: 'Concise & Direct' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
];
